import * as React from 'react'
import { useFormContext } from 'react-hook-form'

import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { Organization as OrganizationIcon } from '@factorialco/f0-react/icons/app'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { NodeUICondition } from 'modules/workflows/components/Node/types'
import { generateUniqueLabel } from 'modules/workflows/components/Node/utils'
import { VariablesConfig } from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import { BasicInputs } from 'modules/workflows/components/Sidepanel/partials/BasicInputs'
import { SidepanelFooter } from 'modules/workflows/components/Sidepanel/partials/SidepanelFooter'
import { SidepanelHeader } from 'modules/workflows/components/Sidepanel/partials/SidepanelHeader'
import { SidepanelWrapper } from 'modules/workflows/components/Sidepanel/partials/SidepanelWrapper'
import { ConditionalFormSchema } from 'modules/workflows/components/Sidepanel/schemas/conditionalFormSchema'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'
import {
  createAllConditionChildEdges,
  createAllConditionChildNodes,
} from 'modules/workflows/lib/flow/adapters'
import { WorkflowsExpression } from 'modules/workflows/lib/types'

import { ConditionPathCard, ConditionPathHeader } from './ConditionPathCard'
import { DEFAULT_COMPARISON } from './constants'

const ConditionalNodeSidepanel = ({
  node,
  variablesConfig,
  conditionsHeaderLabel,
  dynamicInputsHeaderLabel,
}: {
  node: NodeUICondition
  variablesConfig: VariablesConfig
  conditionsHeaderLabel?: string
  dynamicInputsHeaderLabel?: string
}) => {
  const { setNodesUI, setEdgesUI, edgesUI, nodesUI } = useWorkflowContext()
  const { openDialog } = useConfirmationDialog()
  const { handleSubmit, setValue, getValues, watch } = useFormContext<ConditionalFormSchema>()

  const comparisons = watch('comparisons')

  const [isInitialized, setIsInitialized] = React.useState(() => {
    const existingComparisons = getValues('comparisons')
    return existingComparisons.length > 0
  })

  const generateDefaultComparison =
    React.useCallback((): ConditionalFormSchema['comparisons'][number] => {
      const currentComparisons = getValues('comparisons')
      const newLabel = generateUniqueLabel(currentComparisons)

      return {
        label: newLabel,
        priority: currentComparisons.length + 1,
        expression: {
          root: {
            operator: 'and',
            expressions: [DEFAULT_COMPARISON],
          },
        },
      }
    }, [getValues])

  useUnsafeEffect(() => {
    const currentComparisons = getValues('comparisons')

    if (currentComparisons.length === 0) {
      const newComparisons = [...currentComparisons, generateDefaultComparison()]
      setValue('comparisons', newComparisons, { shouldDirty: true })
    }

    // Mark as initialized after ensuring comparisons exist
    setIsInitialized(true)
  }, [node.id, getValues, setValue, generateDefaultComparison])

  if (node.type !== 'condition') return null

  // Helper function for updating a specific comparison's expression
  const setExpression = (newExpression: WorkflowsExpression, label: string) => {
    const currentComparisons = getValues('comparisons')
    const updatedComparisons = currentComparisons.map((comp) =>
      comp.label === label ? { ...comp, expression: newExpression } : comp
    )
    setValue('comparisons', updatedComparisons, { shouldDirty: true })
  }

  // Helper function to add a new comparison
  const addComparison = () => {
    const currentComparisons = getValues('comparisons')

    const newComparisons = [...currentComparisons, generateDefaultComparison()]
    setValue('comparisons', newComparisons, { shouldDirty: true })
  }

  // Helper function to remove a comparison
  const removeComparison = (label: string) => {
    const currentComparisons = getValues('comparisons')
    const updatedComparisons = currentComparisons
      .filter((comp) => comp.label !== label)
      .map((comp, index) => ({ ...comp, priority: index + 1 })) // Reassign priorities
    setValue('comparisons', updatedComparisons, { shouldDirty: true })
  }

  // Closes form by deselecting all nodes
  const handleCancel = () => {
    setNodesUI((prev) => prev.map((n) => ({ ...n, selected: false })))
  }

  // Updates global state and deselects all nodes
  const handleSave = () => {
    const { title, description, comparisons: formComparisons } = getValues()

    // If the title or description is empty, set the default title or description
    // Otherwise, assume it was already set and keep it as is
    const shouldSetDefaultTitle = (title ?? '').trim() === ''
    const shouldSetDefaultDescription = (description ?? '').trim() === ''

    const updatedNode: NodeUICondition = {
      ...node,
      data: {
        ...node.data,
        title: shouldSetDefaultTitle
          ? i18n.t('workflows.building_blocks.condition.title')
          : title || '',
        description: shouldSetDefaultDescription
          ? i18n.t('workflows.building_blocks.condition.description')
          : description || '',
        comparisons: formComparisons,
      },
    }

    setNodesUI((nodes) => {
      // Generate all new child nodes (conditionIf + else) in correct order
      const newChildNodes = createAllConditionChildNodes(
        updatedNode.id,
        updatedNode.data.comparisons
      )

      // All nodes except for children about to be removed
      const nodesWithoutOldChildren = nodes.filter((n) => {
        const isChildNode = edgesUI.some((edge) => edge.source === node.id && edge.target === n.id)
        return !isChildNode
      })

      // Update the main condition node and deselect all nodes
      return [
        ...nodesWithoutOldChildren.map((n) =>
          n.id === updatedNode.id ? { ...updatedNode, selected: false } : { ...n, selected: false }
        ),
        ...newChildNodes,
      ]
    })

    // Update edges: remove old condition split edges and add new ones
    setEdgesUI((edges) => {
      // Remove old edges from this condition to its children
      const edgesWithoutOldSplits = edges.filter((edge) => {
        return edge.source !== node.id
      })

      // Generate all new edges for the updated comparisons (conditionIf + else)
      const newChildEdges = createAllConditionChildEdges(
        updatedNode.id,
        updatedNode.data.comparisons
      )

      return [...edgesWithoutOldSplits, ...newChildEdges]
    })
  }

  const onSubmit = () => {
    handleSubmit(handleSave, (_fields) => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
        time: 3000,
      })
    })()
  }

  // Handle delete with confirmation dialog
  const handleDeleteWithConfirmation = () => {
    openDialog({
      title: i18n.t('workflows.sidepanel.condition.delete_confirmation.title'),
      description: i18n.t('workflows.sidepanel.condition.delete_confirmation.description'),
      confirmLabel: i18n.t('common.delete'),
      onConfirm: handleDelete,
    })
  }

  // Deletes the node and its direct children (special logic for conditional nodes)
  const handleDelete = () => {
    // Find direct children of this node using edges
    const directChildren = nodesUI.filter((childNode) =>
      edgesUI.some((edge) => edge.source === node.id && edge.target === childNode.id)
    )

    const childrenIds = directChildren.map((child) => child.id)
    const nodeToDeleteId = node.id

    // Remove the main node and its direct children from nodes
    setNodesUI((nodes) =>
      nodes.filter((n) => n.id !== nodeToDeleteId && !childrenIds.includes(n.id))
    )

    // Remove all edges connected to the deleted nodes (both incoming and outgoing)
    setEdgesUI((edges) =>
      edges.filter((edge) => {
        const nodesToDelete = [nodeToDeleteId, ...childrenIds]
        return !nodesToDelete.includes(edge.source) && !nodesToDelete.includes(edge.target)
      })
    )
  }

  return (
    <SidepanelWrapper
      width="588px"
      footer={<SidepanelFooter onCancel={handleCancel} onSave={onSubmit} isValid />}
    >
      <SidepanelHeader
        nodeId={node.id}
        handleDelete={handleDeleteWithConfirmation}
        backgroundColor="#CDECEF80"
        icon={
          <OrganizationIcon width={22} height={22} style={{ color: '#07A2AD', strokeWidth: 1.5 }} />
        }
        title={i18n.t('workflows.sidepanel.condition.edit_title')}
        onClose={handleCancel}
      />
      <BasicInputs />
      {isInitialized && comparisons.length > 0 && (
        <>
          <ConditionPathHeader onAddCondition={() => addComparison()} />
          {comparisons.map((comparison, comparisonPos) => (
            <ConditionPathCard
              key={comparison.label}
              comparisonPos={comparisonPos}
              expression={comparison.expression}
              setExpression={(newExpression) => setExpression(newExpression, comparison.label)}
              variablesConfig={variablesConfig}
              priority={comparison.priority}
              onRemove={() => removeComparison(comparison.label)}
              canRemove={comparisons.length > 1}
              conditionsHeaderLabel={conditionsHeaderLabel}
              dynamicInputsHeaderLabel={dynamicInputsHeaderLabel}
            />
          ))}
        </>
      )}
    </SidepanelWrapper>
  )
}

export default ConditionalNodeSidepanel
